import React from "react";
import clsx from "clsx";

// Custom.
import { ParticipantFormProps } from "../global";
import EditDialog from "@vocollege/components/dist/EditDialog";
import Layout from "containers/Layout/Layout";
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_PARTICIPANT,
  CREATE_PARTICIPANT,
  UPDATE_PARTICIPANT,
  GET_COURSE_MOMENTS,
} from "api";
import { GeneralObject } from "@vocollege/app/dist/global";
import { useStyles as useStylesForm } from "ui/styles/form";
import { useStyles } from "./styles";
import {
  getContactName,
  regexPatterns,
} from "@vocollege/app/dist/modules/VoHelpers";

const ParticipantForm: React.FC<ParticipantFormProps> = (props) => {
  const {
    dialog,
    dialogProps = { open: false },
    onComplete,
    onCancel,
    urlParams,
    courseMoment,
  } = props;
  const classesForm = useStylesForm();
  const classes = useStyles();

  // Methods.

  const getAvailableWorkplacesValues = () => {
    return Object.keys(I18n.get.participant.workplaces).map(
      (workplace: string) => ({
        label: I18n.get.participant.workplaces[workplace],
        value: workplace,
      }),
    );
  };

  const getAvailableEmployersValues = () => {
    return Object.keys(I18n.get.participant.employers).map(
      (employer: string) => ({
        label: I18n.get.participant.employers[employer],
        value: employer,
      }),
    );
  };

  const getInitialState = (): GeneralObject => {
    return {
      id: 0,
      firstname: "",
      lastname: "",
      workplace: "",
      workplace_other: "",
      town: "",
      employer_type: "",
      employer: "",
      personal_number: "",
      course_moment: urlParams?.id,
    };
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push({
      key: "content",
      label: I18n.get.misc.content,
      fields: [
        {
          name: "firstname",
          label: I18n.get.user.labels.firstname,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: true,
          },
        },
        {
          name: "lastname",
          label: I18n.get.user.labels.lastname,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: true,
          },
        },
        {
          name: "personal_number",
          label: I18n.get.user.labels.personalNumber,
          type: "text",
          grid: {
            xs: 12,
          },
          validation: {
            validate: (value: any) => {
              if (!value || value === "") {
                return true;
              }
              return (
                !!value.match(regexPatterns.personalNumber) ||
                I18n.get.form.errors.personalNumberInvalid
              );
            },
          },
        },
        {
          name: "workplace",
          label: I18n.get.form.labels.workplace,
          type: "select",
          required: true,
          grid: {
            xs: 12,
          },
          validation: {
            required: true,
          },
          params: {
            availableValues: getAvailableWorkplacesValues,
          },
        },
        {
          name: "workplace_other",
          label: I18n.get.form.labels.workplace,
          type: "text",
          required: true,
          grid: {
            xs: 12,
          },
          validation: {
            required: true,
          },
          params: {
            dependency: {
              workplace: {
                value: ["annat"],
                visibility: true,
              },
            },
          },
        },

        {
          name: "employer_type",
          label: I18n.get.form.labels.employer,
          type: "select",
          required: true,
          grid: {
            xs: 12,
          },
          validation: {
            required: true,
          },
          params: {
            availableValues: getAvailableEmployersValues,
          },
        },
        {
          name: "employer",
          label: I18n.get.participant.labels.employer,
          type: "text",
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          name: "town",
          label: I18n.get.user.labels.town,
          type: "text",
          required: true,
          grid: {
            xs: 12,
            md: 6,
          },
          validation: {
            required: true,
          },
        },
        {
          name: "course_moment",
          label: I18n.get.vocertCourseMoment.label,
          type: "entity_field",
          grid: {
            xs: 12,
          },
          overrideValue: { id: courseMoment?.id },
          params: {
            renderFieldValue: (item: any) => {
              if (item.title) {
                return item?.title;
              }
              return courseMoment?.title || "";
            },
            dialog: {
              types: ["course_moment"],
              renderTitleField: (item: any) => item?.title || "",
              query: GET_COURSE_MOMENTS,
              category: "certCourseMoments.data",
            },
          },
        },
      ],
    });
    return tabs;
  };

  const getForm = () => {
    return (
      <Form
        tabs={getTabs()}
        labels={{
          loading: I18n.get.form.labels.loadingContent,
          create: I18n.get.participant.labels.createParticipant,
          created: I18n.get.participant.messages.participantSaved,
          updated: I18n.get.participant.messages.participantSaved,
          deleted: I18n.get.participant.messages.participantDeleted,
          fieldRequired: I18n.get.form.errors.required,
        }}
        operations={{
          category: "certParticipant",
          get: GET_PARTICIPANT,
          create: CREATE_PARTICIPANT,
          update: UPDATE_PARTICIPANT,
          getOnCreate: GET_COURSE_MOMENTS,
        }}
        paths={{
          back: "/participants",
        }}
        initialState={getInitialState()}
        renderPageTitle={(state) =>
          state.id === 0
            ? I18n.get.participant.labels.createParticipant
            : getContactName(state)
        }
        urlParams={urlParams}
        createParam="skapa"
        classes={{
          paper: "vo-global__content-with-toolbar",
          toolbar: clsx("vo-global__content-toolbar", classesForm.toolbar),
        }}
        // onQueryLoading={(loading) => setFormLoading(loading)}
        // onDataChange={(data) => {
        //   dispatch({ values: data });
        // }}
        onComplete={onComplete}
        onCancel={onCancel}
        toolbarProps={{
          options: {
            cancelButton: { hideLabel: true },
          },
        }}
      />
    );
  };

  return (
    <>
      {dialog && (
        <EditDialog
          {...dialogProps}
          disableActions
          disableCloseButton
          className={classes.dialog}
        >
          {getForm()}
        </EditDialog>
      )}
      {!dialog && <Layout>{getForm()}</Layout>}
    </>
  );
};

export default ParticipantForm;
